import { Component, type ReactNode } from "react";

// After a deploy, content-hashed chunk filenames change. A browser still running
// the previous index.html then 404s when it lazy-loads a route chunk ("Failed to
// fetch dynamically imported module"), the import promise rejects, and with no
// boundary React unmounts the whole tree → blank white page.
//
// This boundary (plus the window 'vite:preloadError' listener in main.tsx) turns
// that into a one-shot reload to pull the fresh index + assets. The time guard
// stops a reload loop if a chunk is genuinely missing (bad deploy), in which case
// we show a small retry card instead of looping.
const RELOAD_KEY = "cortex-chunk-reload-at";
const RELOAD_COOLDOWN_MS = 10_000;

function isChunkLoadError(err: unknown): boolean {
  const msg = (err as Error)?.message || String(err || "");
  return /dynamically imported module|importing a module script failed|error loading dynamically|ChunkLoadError|Loading chunk \d+ failed|Failed to fetch/i.test(msg);
}

function reloadedRecently(): boolean {
  const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
  return Date.now() - last < RELOAD_COOLDOWN_MS;
}

export class ChunkErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(error: unknown): void {
    if (isChunkLoadError(error) && !reloadedRecently()) {
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      window.location.reload();
    }
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", gap: 14,
        fontFamily: "system-ui, sans-serif", color: "#475569", padding: 24, textAlign: "center",
      }}>
        <p style={{ margin: 0, fontSize: 14 }}>This page didn’t load. A new version may have just shipped.</p>
        <button
          onClick={() => { sessionStorage.removeItem(RELOAD_KEY); window.location.reload(); }}
          style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid #cbd5e1", background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
          Reload
        </button>
      </div>
    );
  }
}
